import React, { useState } from 'react';
import * as yup from 'yup';
import axios from 'axios';
import { useHistory } from 'react-router';
import { SaveResponse } from 'helpers/interfaces';
import { IssueServiceType, TypeInfo } from 'helpers/enums';
import moment from 'moment';
import { KeyValuePair } from 'helpers';


interface IContext {
    isSaving: boolean
    productSuppliers: KeyValuePair<string, string>[]
    saveTicket: (ticket: INewTicket) => Promise<void>
    updateProductSuppliers: (productSuppliers: KeyValuePair<string, string>[]) => void
}

export let attachmentSchema = yup.object({
    fileId: yup.string().default('').required('Attachment not valid'),
    fileName: yup.string().default('')
})

export let newTicketSchema = yup.object({
    ticketTypeId: yup.string().default('').required("Please choose issue typology"),
    ticketPriorityId: yup.string().default('').required("Please choose issue priority"),
    supplier: yup.string().default('').required("Please choose Supplier"),
    title: yup.string().default('').required("Please set a title"),
    message: yup.string().default('').required("Please provide description of issue"),
    attachments: yup.array(attachmentSchema).default([]),

    typeInfo: yup.string().default(''),

    reporter: yup.string().default('').required('Type in the name of a customer or colleague'),
    reporterName: yup.string().default(''),
    deliveryNote: yup.string().default('')
        .when("typeInfo", {
            is: TypeInfo.Delivery,
            then: yup.string().default("").required("Please set delivery note"),
            otherwise: yup.string().default("")
        }),
    invoiceDate: yup.date().nullable().default(null),

    invoiceNumber: yup.string().default('')
        .when("typeInfo", {
            is: TypeInfo.Delivery,
            then: yup.string().default("").required("Please provide invoice number"),
            otherwise: yup.string().default("")
        }),

    invoiceQuantity: yup.string().default('')
        .when("issueServiceType", {
            is: IssueServiceType.OvershippedItems,
            then: yup.string().default('')
                .test('ok', 'Quantity must be greater or equal to 0', (value) => !value || Number(value) >= 0)
                .required('Please provide invoice quantity'),
            otherwise: yup.string().default('')
        })
        .when("issueServiceType", {
            is: IssueServiceType.MissingItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required('Please provide invoice quantity'),
            otherwise: yup.string().default('')
        })
        .when("issueServiceType", {
            is: IssueServiceType.DamagedItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required('Please provide invoice quantity'),
            otherwise: yup.string().default('')
        }),

    receivedQuantity: yup.string().default('')
        .when("issueServiceType", {
            is: IssueServiceType.OvershippedItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required('Please provide received quantity'),
            otherwise: yup.string().default('')
        })
        .when("issueServiceType", {
            is: IssueServiceType.MissingItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than or equal to 0', (value) => !value || Number(value) >= 0).required('Please provide received quantity'),
            otherwise: yup.string().default('')
        }),

    damagedItemQuantity: yup.string()
        .when("issueServiceType", {
            is: IssueServiceType.DamagedItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required('Please provide damaged item quantity'),
            otherwise: yup.string().default('')
        })
        .default(''),

    productSku: yup.string().default('')
        .when("typeInfo", {
            is: TypeInfo.Product,
            then: yup.string().default('').required("Product is mandatory"),
            otherwise: yup.string().default('')
        })
        .when("issueServiceType", {
            is: IssueServiceType.OvershippedItems,
            then: yup.string().default('').required("Product is mandatory"),
            otherwise: yup.string().default('')
        })
        .when("issueServiceType", {
            is: IssueServiceType.MissingItems,
            then: yup.string().default('').required("Product is mandatory"),
            otherwise: yup.string().default('')
        })
        .when("issueServiceType", {
            is: IssueServiceType.DamagedItems,
            then: yup.string().default('').required("Product is mandatory"),
            otherwise: yup.string().default('')
        })
        .default(''),

    hasSerialNumber: yup.boolean().default(false),

    productBrand: yup.string().default(''),
    productDescription: yup.string().default(''),
    productQuantity: yup.string().default('')
        .when("typeInfo", {
            is: TypeInfo.Product,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required("Product quantity is mandatory"),
            otherwise: yup.string().default('')
        }),

    productSupplierCode: yup.string().default(''),
    productSupplierName: yup.string().default(''),

    // radio button
    hasPurchaseReceipt: yup.string()
        .when("typeInfo", {
            is: TypeInfo.Product,
            then: yup.string().oneOf(["true", "false"], "Please choose one option").default(''),
            otherwise: yup.string().default('')
        }),
    canYouRepairProductYourself: yup.string()
        .when("typeInfo", {
            is: TypeInfo.Product,
            then: yup.string().oneOf(["true", "false"], "Please choose one option").default(''),
            otherwise: yup.string().default('')
        }),
    productIsNew: yup.string().default('')
        .when("typeInfo", {
            is: TypeInfo.Product,
            then: yup.string().oneOf(["new", "used"], "Please choose one option").default(''),
            otherwise: yup.string().default('')
        }),

    productIsUnderWarranty: yup.string().default('')
        .when("typeInfo", {
            is: TypeInfo.Product,
            then: yup.string().oneOf(["true", "false"], "Please choose one option").default(''),
            otherwise: yup.string().default('')
        }),

    issueServiceType: yup.string().default('')
        .when("typeInfo", {
            is: TypeInfo.Delivery,
            then: yup.string().default('').oneOf([IssueServiceType.None, IssueServiceType.DamagedPackages, IssueServiceType.MissingPackages, IssueServiceType.OvershippedItems, IssueServiceType.MissingItems, IssueServiceType.DamagedItems], "Please choose one option"),
            otherwise: yup.string().default(IssueServiceType.None)
        }),

    numberOfPackages: yup.string().default('')
        .when("issueServiceType", {
            is: IssueServiceType.MissingPackages,
            then: yup.string().default('')
                .test('numberOfPackages', 'Too much packages are provided', val => val.length < 3)
                .test('numberOfPackages', 'Number of packages must be greater than 0', val => val > '0')
                .required('Please provide number of packages'),
            otherwise: yup.string().default('')
        })
        .when("issueServiceType", {
            is: IssueServiceType.DamagedPackages,
            then: yup.string().default('')
                .test('numberOfPackages', 'Too much packages are provided', val => val.length < 3)
                .test('numberOfPackages', 'Number of packages must be greater than 0', val => val > '0')
                .required('Please provide number of packages'),
            otherwise: yup.string().default('')
        }),

    packageNumbers: yup.array(yup.string().default('')).default([])
        .when("issueServiceType", {
            is: IssueServiceType.MissingPackages,
            then: yup.array(yup.string().default('').required('Please provide valid package number')).default([]).min(1, 'Please provide package number'),
            otherwise: yup.array(yup.string().default('')).default([])
        })
        .when("issueServiceType", {
            is: IssueServiceType.DamagedPackages,
            then: yup.array(yup.string().default('').required('Please provide valid package number')).default([]).min(1, 'Please provide package number'),
            otherwise: yup.array(yup.string().default('')).default([])
        }),



    //Text
    serialNumber: yup.string().default(''),

    suggestedAction: yup.string().default(''),
    endCustomersHasRegisteredProduct: yup.boolean().default(false),
    endCustomerEmailAddress: yup.string().default(''),
    productQRCode: yup.string().default(""),

    purchaseCountry: yup.string().default(''),
    purchaseDate: yup.date().default(null).nullable()
});

export type INewTicket = yup.InferType<typeof newTicketSchema>;

export let newTicketContext = React.createContext({} as IContext);

let { Provider } = newTicketContext;

let NewTicketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let history = useHistory();

    let [isSaving, setSaving] = useState(false);
    let [productSuppliers, setProductSuppliers] = useState<KeyValuePair<string, string>[]>([]);

    let saveTicket = async (ticket: INewTicket) => {
        try {
            setSaving(true);

            if (ticket.typeInfo === TypeInfo.Product && ticket.attachments.length <= 0) {
                let label = ticket.hasPurchaseReceipt === 'false'
                    ? 'remember to add packaging label picture'
                    : 'remember to add packaging label and purchase receipt pictures';

                if (window.confirm(`Missing attachments; ${label}. Do you want to continue?`) === false) {
                    setSaving(false);
                    return;
                }
            }

            if (ticket.typeInfo === TypeInfo.Delivery) {
                if (ticket.issueServiceType === IssueServiceType.OvershippedItems && Number(ticket.invoiceQuantity) === 0) {
                    if (window.confirm(`Invoice quantity should be greater than 0. Do you want to continue?`) === false) {
                        return;
                    }
                }
            }

            let request = {
                ...ticket,
                invoiceDate: moment(ticket.invoiceDate).utc(true)
            }

            let res = await axios.post<SaveResponse>('/api/Ticket/SaveNewTicket', request);
            history.push(`/ticket/${res.data.id}/`);

        } catch (err) {
            setSaving(false);
        }
    }

    let updateProductSuppliers = (productSuppliers: KeyValuePair<string, string>[]) => {
        setProductSuppliers(productSuppliers);
    }


    return (
        <Provider
            value={{
                isSaving,
                productSuppliers,
                saveTicket,
                updateProductSuppliers
            }}
        >
            {children}
        </Provider>
    )
}

export default NewTicketProvider;